import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Crown } from "lucide-react";
import { Panel, Empty } from "./shared";

function LeaderApp({ a, onDecide }) {
  return (
    <div className="py-3 border-b border-[#ECE7D8] last:border-0" data-testid={`leader-app-${a.id}`}>
      <div className="font-bold">{a.name} <span className="text-xs font-normal text-muted-foreground">· {a.position}</span></div>
      <div className="text-sm">{a.organization}</div>
      <div className="text-xs text-muted-foreground">{a.email}{a.organization_website ? ` · ${a.organization_website}` : ""}</div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => onDecide("approve")} data-testid={`approve-leader-${a.id}`} className="h-8 px-4 rounded-full border-2 border-ink text-xs font-bold press" style={{ background: "#B8FF3B" }}>Approve</button>
        <button onClick={() => onDecide("deny")} data-testid={`deny-leader-${a.id}`} className="h-8 px-4 rounded-full border-2 border-ink text-xs font-bold press bg-white">Deny</button>
      </div>
    </div>
  );
}

export function LeadersTab() {
  const [apps, setApps] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [newOrg, setNewOrg] = useState({ name: "", website: "" });

  const load = useCallback(() => {
    apiGet("/admin/org-leader-applications").then(setApps).catch(() => {});
    apiGet("/admin/organizations").then(setOrgs).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      const res = await apiPost(`/admin/org-leader-applications/${id}/decide?decision=${decision}`, {});
      if (decision === "approve") {
        const how = res.account_exists ? "their existing account was upgraded" : "they can now sign up with this email";
        toast.success(`Approved — ${how}.${res.emailed ? " Notified by email." : " Email not sent — let them know manually."}`, { duration: 10000 });
      } else toast.success("Denied");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const createOrg = async () => {
    if (!newOrg.name.trim()) return;
    try { await apiPost("/admin/organizations", newOrg); toast.success("Organization created"); setNewOrg({ name: "", website: "" }); load(); }
    catch (e) { toast.error(e.message); }
  };

  const pending = apps.filter((a) => a.status === "pending");

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="leaders-tab">
      <Panel title="Organization-leader requests" accent="#FF5722" icon={Crown}>
        {pending.length === 0 && <Empty text="No pending requests." />}
        {pending.map((a) => <LeaderApp key={a.id} a={a} onDecide={(d) => decide(a.id, d)} />)}
      </Panel>

      <Panel title="Organizations" accent="#31B8FF" icon={Building2}>
        <div className="flex gap-2 mb-4">
          <Input value={newOrg.name} onChange={(e) => setNewOrg((o) => ({ ...o, name: e.target.value }))} placeholder="New organization name" className="border-2 border-ink h-10 rounded-xl" data-testid="new-org-name" />
          <Button onClick={createOrg} className="rounded-full border-2 border-ink press font-bold" data-testid="create-org">Add</Button>
        </div>
        <div className="space-y-2 max-h-80 overflow-auto">
          {orgs.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-xl border-2 border-ink p-3 bg-white" data-testid={`org-item-${o.id}`}>
              <div>
                <div className="font-bold text-sm">{o.name}</div>
                <div className="text-xs text-muted-foreground">{o.student_count} students · {o.mentor_count} mentors</div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-ink" style={{ background: "#B8FF3B" }}>{o.status}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
